import { useState } from 'react'
import {
  HStack,
  VStack,
  Select,
  Input,
  Button,
} from '@chakra-ui/react'
import { STRINGS } from '../../resources'
import styles from './FilterBuilder.module.css'

export interface ColumnFilter {
  id: string
  column: string
  operator: string
  value: string
}

interface FilterBuilderProps {
  filters: ColumnFilter[]
  onFiltersChange: (filters: ColumnFilter[]) => void
}

const COLUMNS = [
  { value: 'cve', label: STRINGS.FILTER_COLUMNS.CVE },
  { value: 'packageName', label: STRINGS.FILTER_COLUMNS.PACKAGE_NAME },
  { value: 'severity', label: STRINGS.FILTER_COLUMNS.SEVERITY },
  { value: 'cvss', label: STRINGS.FILTER_COLUMNS.CVSS },
  { value: 'kaiStatus', label: STRINGS.FILTER_COLUMNS.ANALYSIS_STATUS },
  { value: 'attackVector', label: STRINGS.FILTER_COLUMNS.ATTACK_VECTOR },
  { value: 'exploitAvailable', label: STRINGS.FILTER_COLUMNS.EXPLOIT },
  { value: 'fixAvailable', label: STRINGS.FILTER_COLUMNS.FIX },
  { value: 'groupName', label: STRINGS.FILTER_COLUMNS.GROUP_NAME },
  { value: 'imageName', label: STRINGS.FILTER_COLUMNS.IMAGE_NAME },
]

const OPERATORS: Record<string, { value: string; label: string }[]> = {
  text: [
    { value: 'equals', label: STRINGS.FILTER_OPERATORS.EQUALS },
    { value: 'contains', label: STRINGS.FILTER_OPERATORS.CONTAINS },
    { value: 'startsWith', label: STRINGS.FILTER_OPERATORS.STARTS_WITH },
    { value: 'endsWith', label: STRINGS.FILTER_OPERATORS.ENDS_WITH },
    { value: 'notContains', label: STRINGS.FILTER_OPERATORS.NOT_CONTAINS },
    { value: 'notEquals', label: STRINGS.FILTER_OPERATORS.NOT_EQUALS },
  ],
  number: [
    { value: 'equals', label: '=' },
    { value: 'gt', label: STRINGS.FILTER_OPERATORS.GT },
    { value: 'gte', label: STRINGS.FILTER_OPERATORS.GTE },
    { value: 'lt', label: STRINGS.FILTER_OPERATORS.LT },
    { value: 'lte', label: STRINGS.FILTER_OPERATORS.LTE },
    { value: 'notEquals', label: '!=' },
  ],
  severity: [
    { value: 'equals', label: STRINGS.FILTER_OPERATORS.IS },
    { value: 'notEquals', label: STRINGS.FILTER_OPERATORS.IS_NOT },
  ],
  analysisStatus: [
    { value: 'equals', label: STRINGS.FILTER_OPERATORS.IS },
    { value: 'notEquals', label: STRINGS.FILTER_OPERATORS.IS_NOT },
  ],
  attackVector: [
    { value: 'equals', label: STRINGS.FILTER_OPERATORS.IS },
    { value: 'notEquals', label: STRINGS.FILTER_OPERATORS.IS_NOT },
  ],
  boolean: [
    { value: 'equals', label: STRINGS.FILTER_OPERATORS.IS },
  ],
}

const COLUMN_TYPES: Record<string, 'text' | 'number' | 'severity' | 'analysisStatus' | 'attackVector' | 'boolean'> = {
  cve: 'text',
  packageName: 'text',
  severity: 'severity',
  cvss: 'number',
  kaiStatus: 'analysisStatus',
  attackVector: 'attackVector',
  exploitAvailable: 'boolean',
  fixAvailable: 'boolean',
  groupName: 'text',
  imageName: 'text',
}

const SEVERITY_OPTIONS = ['critical', 'high', 'medium', 'low']
const ANALYSIS_STATUS_OPTIONS = [
  { value: 'pending', label: STRINGS.ANALYSIS_STATUS.PENDING_REVIEW },
  { value: 'ai-cleared', label: STRINGS.ANALYSIS_STATUS.AI_NO_RISK },
  { value: 'manual-cleared', label: STRINGS.ANALYSIS_STATUS.MANUAL_NO_RISK },
]
const ATTACK_VECTOR_OPTIONS = [
  STRINGS.ATTACK_VECTOR.NETWORK,
  STRINGS.ATTACK_VECTOR.ADJACENT,
  STRINGS.ATTACK_VECTOR.LOCAL,
  STRINGS.ATTACK_VECTOR.PHYSICAL,
]
const BOOLEAN_OPTIONS = [
  { value: 'yes', label: STRINGS.BOOLEAN.YES },
  { value: 'no', label: STRINGS.BOOLEAN.NO },
]

export function FilterBuilder({ filters, onFiltersChange }: FilterBuilderProps) {
  const [column, setColumn] = useState('cve')
  const [operator, setOperator] = useState('equals')
  const [value, setValue] = useState('')
  const [isAdding, setIsAdding] = useState(false)

  const columnType = COLUMN_TYPES[column] || 'text'
  const operators = OPERATORS[columnType]

  const handleColumnChange = (newColumn: string) => {
    setColumn(newColumn)
    const newType = COLUMN_TYPES[newColumn] || 'text'
    setOperator(OPERATORS[newType][0].value)
    setValue('')
  }

  const addFilter = () => {
    if (!value.trim() || isAdding) return

    setIsAdding(true)
    const newFilter: ColumnFilter = {
      id: `${Date.now()}`,
      column,
      operator,
      value: value.trim(),
    }

    onFiltersChange([...filters, newFilter])
    setValue('')
    setIsAdding(false)
  }

  return (
    <VStack className={styles.container} align="stretch">
      <HStack className={styles.inputRow}>
        <Select
          size="sm"
          value={column}
          onChange={(e) => handleColumnChange(e.target.value)}
          className={styles.select}
          w="130px"
        >
          {COLUMNS.map((col) => (
            <option key={col.value} value={col.value}>
              {col.label}
            </option>
          ))}
        </Select>

        <Select
          size="sm"
          value={operator}
          onChange={(e) => setOperator(e.target.value)}
          className={styles.select}
          w="120px"
        >
          {operators.map((op) => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </Select>

        {columnType === 'severity' ? (
          <Select
            size="sm"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={STRINGS.FILTER_BUILDER.SELECT_PLACEHOLDER}
            className={styles.select}
            w="120px"
          >
            {SEVERITY_OPTIONS.map((sev) => (
              <option key={sev} value={sev}>
                {sev}
              </option>
            ))}
          </Select>
        ) : columnType === 'analysisStatus' ? (
          <Select
            size="sm"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={STRINGS.FILTER_BUILDER.SELECT_PLACEHOLDER}
            className={styles.select}
            w="140px"
          >
            {ANALYSIS_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        ) : columnType === 'attackVector' ? (
          <Select
            size="sm"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={STRINGS.FILTER_BUILDER.SELECT_PLACEHOLDER}
            className={styles.select}
            w="120px"
          >
            {ATTACK_VECTOR_OPTIONS.map((av) => (
              <option key={av} value={av}>
                {av}
              </option>
            ))}
          </Select>
        ) : columnType === 'boolean' ? (
          <Select
            size="sm"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={STRINGS.FILTER_BUILDER.SELECT_PLACEHOLDER}
            className={styles.select}
            w="120px"
          >
            {BOOLEAN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        ) : (
          <Input
            size="sm"
            placeholder={columnType === 'number' ? STRINGS.FILTER_BUILDER.VALUE_PLACEHOLDER : STRINGS.FILTER_BUILDER.TEXT_PLACEHOLDER}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addFilter()}
            className={styles.input}
            w="150px"
            type={columnType === 'number' ? 'number' : 'text'}
          />
        )}

        <Button
          size="sm"
          colorScheme="blue"
          onClick={addFilter}
          className={styles.addButton}
          isDisabled={isAdding || (columnType === 'text' || columnType === 'number' ? !value.trim() : false)}
        >
          {STRINGS.FILTERS.ADD_FILTER}
        </Button>
      </HStack>

    </VStack>
  )
}

// Helper to extract derived field values
function getDerivedFieldValue(item: Record<string, unknown>, column: string): string | boolean | null {
  const riskFactors = item.riskFactors as string[] | undefined

  if (column === 'attackVector') {
    if (!riskFactors) return null
    for (const rf of riskFactors) {
      if (rf.startsWith('Attack Vector:')) {
        return rf.split(':')[1]?.trim() || null
      }
    }
    return null
  }

  if (column === 'exploitAvailable') {
    if (!riskFactors) return false
    return riskFactors.some((rf) => rf.toLowerCase().includes('exploit'))
  }

  if (column === 'fixAvailable') {
    if (!riskFactors) return false
    return riskFactors.some((rf) => rf.toLowerCase().includes('fix'))
  }

  return null
}

// Helper to apply filters to data
export function applyColumnFilters<T>(
  data: T[],
  filters: ColumnFilter[]
): T[] {
  if (filters.length === 0) return data

  return data.filter((item) =>
    filters.every((filter) => {
      const itemRecord = item as Record<string, unknown>

      // Handle derived fields
      if (['attackVector', 'exploitAvailable', 'fixAvailable'].includes(filter.column)) {
        const derivedValue = getDerivedFieldValue(itemRecord, filter.column)

        if (filter.column === 'attackVector') {
          if (derivedValue === null) return filter.operator === 'notEquals'
          const strValue = String(derivedValue).toLowerCase()
          const filterValue = filter.value.toLowerCase()
          return filter.operator === 'equals' ? strValue === filterValue : strValue !== filterValue
        }

        // Boolean fields (exploitAvailable, fixAvailable)
        const boolValue = derivedValue === true
        const filterBool = filter.value === 'yes'
        return boolValue === filterBool
      }

      // Handle regular fields
      const fieldValue = itemRecord[filter.column]
      const filterValue = filter.value.toLowerCase()

      if (fieldValue === null || fieldValue === undefined) return false

      const strValue = String(fieldValue).toLowerCase()
      const numValue = typeof fieldValue === 'number' ? fieldValue : parseFloat(strValue)

      switch (filter.operator) {
        case 'contains':
          return strValue.includes(filterValue)
        case 'notContains':
          return !strValue.includes(filterValue)
        case 'equals':
          return strValue === filterValue
        case 'notEquals':
          return strValue !== filterValue
        case 'startsWith':
          return strValue.startsWith(filterValue)
        case 'endsWith':
          return strValue.endsWith(filterValue)
        case 'gt':
          return !isNaN(numValue) && numValue > parseFloat(filter.value)
        case 'gte':
          return !isNaN(numValue) && numValue >= parseFloat(filter.value)
        case 'lt':
          return !isNaN(numValue) && numValue < parseFloat(filter.value)
        case 'lte':
          return !isNaN(numValue) && numValue <= parseFloat(filter.value)
        default:
          return true
      }
    })
  )
}

// Export helper to format filter labels for display
export function getFilterLabel(filter: ColumnFilter): string {
  const col = COLUMNS.find((c) => c.value === filter.column)?.label || filter.column
  const op = OPERATORS[COLUMN_TYPES[filter.column]]?.find((o) => o.value === filter.operator)?.label || filter.operator
  const columnType = COLUMN_TYPES[filter.column]
  // Only wrap text values in quotes
  const displayValue = columnType === 'text' ? `"${filter.value}"` : filter.value
  return `${col} ${op} ${displayValue}`
}
